import React, { useEffect, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { Store } from "../Store";
import { Box, Text, Container, Button } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useAuth } from "../../Hooks/useAuth";
import { Footer } from "../../components/Footer";
import { Carousel } from "../../components/Carousel";
import { ReviewsCarousel } from "../../components/ReviewsCarousel";
import { Link } from "react-router-dom";
import { ProductsCarousel } from "../../components/ProductsCarousel";
import { FiTruck, FiPercent, FiRepeat, FiCreditCard, FiShield, FiMapPin, FiAward } from "react-icons/fi";
import { CartContext } from "../../Context/CartContext";
import { AddToCartPopUp } from "../../components/AddToCartPopUp";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { api } from "../../services/api";
import { EventCalculatorWizard } from "../../components/EventCalculator";

// ─── Design tokens ────────────────────────────────────────────────────────────
const GREEN  = "#004a33";
const ORANGE = "#fc8f04";
const BG     = "#f8f8f8";

// ─── Scroll reveal ────────────────────────────────────────────────────────────
const MotionBox = motion(Box);

function Reveal({ children, delay = 0 }) {
  return (
    <MotionBox
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </MotionBox>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────
function SectionHeader({ eyebrow, title, subtitle, cta, ctaHref }) {
  return (
    <Box
      display="flex"
      flexDir={{ base: "column", md: "row" }}
      alignItems={{ base: "flex-start", md: "flex-end" }}
      justifyContent="space-between"
      mb={{ base: "28px", md: "48px" }}
      gap={{ base: "8px", md: "0" }}
    >
      <Box>
        {eyebrow && (
          <Text
            fontSize="11px"
            fontWeight="800"
            color={ORANGE}
            textTransform="uppercase"
            letterSpacing="0.14em"
            mb="10px"
          >
            {eyebrow}
          </Text>
        )}
        <Text
          fontSize={{ base: "26px", md: "38px" }}
          fontWeight="900"
          color={GREEN}
          letterSpacing="-0.03em"
          lineHeight="1.1"
          mb="8px"
        >
          {title}
        </Text>
        {subtitle && (
          <Text fontSize={{ base: "14px", md: "15px" }} color="gray.500" fontWeight="500">
            {subtitle}
          </Text>
        )}
      </Box>
      {cta && ctaHref && (
        <Link to={ctaHref}>
          <Button
            size="sm"
            variant="outline"
            borderColor={`${GREEN}33`}
            color={GREEN}
            fontWeight="700"
            fontSize="13px"
            borderRadius="99px"
            px="18px"
            _hover={{ bg: `${GREEN}08`, borderColor: GREEN }}
            flexShrink={0}
          >
            {cta} →
          </Button>
        </Link>
      )}
    </Box>
  );
}

// ─── Divider between sections ─────────────────────────────────────────────────
function SectionDivider() {
  return <Box h="1px" bg="rgba(0,74,51,.06)" mx={{ base: "16px", md: "0" }} />;
}

// ─── Value strip — brand promise, no fabricated metrics ───────────────────────
const VALUE_PROPS = [
  { icon: FiAward,  title: "Curadoria especializada", sub: "Produtores selecionados a dedo" },
  { icon: FiShield, title: "Sem risco, sem negociação", sub: "Preço justo, sempre" },
  { icon: FiMapPin, title: "Entrega em toda BH",        sub: "E Região Metropolitana" },
];

function ValueStrip() {
  return (
    <Box bg="white" borderTop="1px solid rgba(0,74,51,.07)" borderBottom="1px solid rgba(0,74,51,.07)">
      <Container maxW="1100px" px={{ base: "16px", md: "32px" }}>
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }}
          gap={{ base: "16px", sm: "8px" }}
          py={{ base: "20px", md: "26px" }}
        >
          {VALUE_PROPS.map((v, i) => (
            <Box
              key={i}
              display="flex"
              alignItems="center"
              gap="12px"
              justifyContent={{ base: "flex-start", sm: "center" }}
            >
              <Box
                flexShrink={0}
                w="34px"
                h="34px"
                borderRadius="10px"
                bg="rgba(0,74,51,.06)"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <v.icon size={16} color={GREEN} />
              </Box>
              <Box>
                <Text fontWeight="800" fontSize="13px" color={GREEN} lineHeight="1.2">
                  {v.title}
                </Text>
                <Text fontWeight="500" fontSize="12px" color="gray.500">
                  {v.sub}
                </Text>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

// ─── Trust bar (policies, refined presentation) ────────────────────────────────
const TRUST_ITEMS = [
  { icon: FiTruck,      title: "Frete Grátis",     sub: "Acima de R$ 120"   },
  { icon: FiPercent,    title: "5% Desconto",       sub: "Cupom: PRIMEIRA"   },
  { icon: FiRepeat,     title: "1ª Troca Grátis",  sub: "Sem burocracia"     },
  { icon: FiCreditCard, title: "Parcelado",          sub: "Até 4× sem juros" },
];

function TrustBar() {
  return (
    <Box bg={BG}>
      <Container maxW="1400px">
        <Box
          display="grid"
          gridTemplateColumns={{ base: "repeat(2,1fr)", md: "repeat(4,1fr)" }}
          gap={{ base: "10px", md: "0" }}
          p={{ base: "14px", md: "0" }}
        >
          {TRUST_ITEMS.map((item, i) => (
            <Box
              key={i}
              display="flex"
              flexDir={{ base: "column", md: "row" }}
              alignItems="center"
              textAlign={{ base: "center", md: "left" }}
              gap={{ base: "8px", md: "12px" }}
              py={{ base: "16px", md: "22px" }}
              px={{ base: "12px", md: "24px" }}
              bg={{ base: "white", md: "transparent" }}
              borderRadius={{ base: "14px", md: "0" }}
              borderRight={{
                base: "none",
                md: i < 3 ? "1px solid rgba(0,74,51,.08)" : "none",
              }}
            >
              <Box
                flexShrink={0}
                w={{ base: "32px", md: "36px" }}
                h={{ base: "32px", md: "36px" }}
                borderRadius="10px"
                bg="rgba(0,74,51,.06)"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <item.icon size={16} color={GREEN} />
              </Box>
              <Box>
                <Text fontWeight="800" fontSize={{ base: "12px", md: "13px" }} color={GREEN} lineHeight="1.2">
                  {item.title}
                </Text>
                <Text fontWeight="500" fontSize={{ base: "11px", md: "12px" }} color="gray.500">
                  {item.sub}
                </Text>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export function Home() {
  const { isAuthenticated } = useAuth();
  const { showCartPopUp } = useContext(CartContext);
  const [productsByType, setProductsByType] = useState({});
  const { hash } = useLocation();

  useEffect(() => {
    async function fetchProducts() {
      const response = await api.get("/");
      setProductsByType(response.data.products_by_type);
    }
    fetchProducts();
  }, []);

  useEffect(() => { isAuthenticated(); }, []);

  useEffect(() => {
    if (hash !== "#store") return;
    const timer = setTimeout(() => {
      const el = document.getElementById("storeSection");
      if (el) window.scrollTo({ top: el.offsetTop - 160, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, [hash]);

  const promoInfos = [
    {
      url: require("../../assets/images/promo1.webp"),
      bannerUrl: `/produtos?filter=${encodeURIComponent(JSON.stringify(["Bebidas"]))}`,
    },
    {
      url: require("../../assets/images/promo2.webp"),
      bannerUrl: `/produtos/Kit+Empresa/${encodeURIComponent("Caixa Misteriosa").replace(/%20/g, "+")}`,
    },
    {
      url: require("../../assets/images/promo3.webp"),
      bannerUrl: `/produtos/Kit+verão/${encodeURIComponent("BH Drink Experience").replace(/%20/g, "+")}`,
    },
  ];

  return (
    <>
      <Navbar />

      {/* ── Event Calculator hero ─────────────────────────────────────────── */}
      <Box mt="0" position="relative" overflowX="hidden" maxW="100vw">
        <EventCalculatorWizard variant="hero" />
      </Box>

      {/* ── Value strip ────────────────────────────────────────────────────── */}
      <ValueStrip />

      {/* ── Campaign carousel (contained, secondary) ────────────────────────── */}
      <Reveal>
        <Box bg={BG} py={{ base: "24px", md: "40px" }}>
          <Container maxW="1400px" px={{ base: "16px", md: "32px" }}>
            <Box borderRadius="20px" overflow="hidden" boxShadow="0 4px 20px rgba(0,74,51,.08)">
              <Carousel />
            </Box>
          </Container>
        </Box>
      </Reveal>

      {/* ── Trust bar ──────────────────────────────────────────────────────── */}
      <Reveal>
        <TrustBar />
      </Reveal>

      {/* ── Categories (Store) ─────────────────────────────────────────────── */}
      <Reveal>
        <Box id="storeSection" bg={BG} py={{ base: "48px", md: "88px" }}>
          <Store />
        </Box>
      </Reveal>

      <SectionDivider />

      {/* ── Mais Vendidos ──────────────────────────────────────────────────── */}
      <Reveal>
        <Box bg="white" py={{ base: "48px", md: "96px" }}>
          <Container maxW="1400px" px={{ base: "16px", md: "32px" }}>
            <SectionHeader
              eyebrow="Os favoritos"
              title="Mais Vendidos"
              subtitle="As bebidas mais apreciadas pelos nossos clientes"
              cta="Ver todos"
              ctaHref={`/produtos?filter=${encodeURIComponent(JSON.stringify(["Mais Vendidos"]))}`}
            />
            <ProductsCarousel typeName="Destaques" products={productsByType["Destaques"]} />
          </Container>
        </Box>
      </Reveal>

      <SectionDivider />

      {/* ── Promo banners ──────────────────────────────────────────────────── */}
      <Reveal>
        <Box bg={BG} py={{ base: "44px", md: "88px" }}>
          <Container maxW="1400px" px={{ base: "16px", md: "32px" }}>
            <SectionHeader
              eyebrow="Curadoria Boozen"
              title="Seleções pensadas pra cada ocasião"
              subtitle="Kits e combinações escolhidos pela nossa equipe"
            />
            <Box
              display="grid"
              gridTemplateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
              gap={{ base: "14px", md: "20px" }}
            >
              {promoInfos.map((promo, i) => (
                <Link to={promo.bannerUrl} key={i}>
                  <Box
                    overflow="hidden"
                    borderRadius="20px"
                    transition="all 0.3s cubic-bezier(0.4,0,0.2,1)"
                    boxShadow="0 4px 16px rgba(0,74,51,.08)"
                    _hover={{ transform: "scale(1.025)", boxShadow: "0 14px 36px rgba(0,74,51,.16)" }}
                    h={{ base: "200px", md: "260px" }}
                  >
                    <LazyLoadImage
                      src={promo.url || require("../../assets/images/placeholder.webp")}
                      alt="Boozen Promo"
                      effect="blur"
                      style={{ objectFit: "cover", width: "100%", height: "100%" }}
                      wrapperProps={{ style: { height: "100%", transitionDelay: "1s" } }}
                      loading="lazy"
                    />
                  </Box>
                </Link>
              ))}
            </Box>
          </Container>
        </Box>
      </Reveal>

      <SectionDivider />

      {/* ── Novidades ──────────────────────────────────────────────────────── */}
      <Reveal>
        <Box bg="white" py={{ base: "48px", md: "96px" }}>
          <Container maxW="1400px" px={{ base: "16px", md: "32px" }}>
            <SectionHeader
              eyebrow="Recém-chegados"
              title="Acabaram de Chegar"
              subtitle="Últimos lançamentos da nossa loja"
              cta="Ver novidades"
              ctaHref={`/produtos?filter=${encodeURIComponent(JSON.stringify(["Novidades"]))}`}
            />
            <ProductsCarousel typeName="Lançamentos" products={productsByType["Lançamentos"]} />
          </Container>
        </Box>
      </Reveal>

      <SectionDivider />

      {/* ── Depoimentos ────────────────────────────────────────────────────── */}
      <Reveal>
        <Box bg={BG} py={{ base: "48px", md: "96px" }}>
          <Container maxW="1400px" px={{ base: "16px", md: "32px" }}>
            <SectionHeader
              eyebrow="Quem já comprou"
              title="Depoimentos"
              subtitle="O que nossos clientes estão falando"
            />
            <Box maxW="100%">
              <ReviewsCarousel />
            </Box>
          </Container>
        </Box>
      </Reveal>

      <Footer />
      {showCartPopUp && <AddToCartPopUp />}
    </>
  );
}
